// useOtpCodeVerification.ts
//
// Shared OTP code-verification surface for the phone (VerifyPasscodeView) and
// email (VerifyEmailCodeView) verify screens. Both screens share the exact same
// post-verify routing logic and the per-code locked-state / attempt-lifecycle
// helpers; only the API calls, analytics, and copy differ. This composable owns
// the genuinely-shared bits so the two views stop duplicating them.
//
// Channel-specific behavior is injected into handleVerifyResponse via callbacks:
//   - advanceFlow(): the flow-navigation advance (same impl, but each view holds
//     its own useFlowNavigation() instance, so it's passed in)
//   - clearCode(): clears that view's code ref (smsCode / emailCode)
//   - onPassed(): the legacy flag-OFF "Passed" handling — phone just advances,
//     email shows a toast then advances.
import { computed, getCurrentInstance, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useDeviceAuthStore } from '@Stores/deviceAuth.store';
import { OtpStatus } from '@Enums/otpStatus.enum';
import { MfaStatus } from '@Enums/mfaStatus.enum';
import { handleStatusCode } from '@Utils/statusCodeHandler';
import type { OtpSendResponse, OtpVerifyResponse } from '@Types/otp.interface';

/**
 * Shown when a SEND/resend is rejected with a cooldown (the per-destination issuance
 * cap was hit). The user can't get a new code right now; if multiple channels are
 * available they may try a different one, since the cooldown is per destination.
 */
export const OTP_COOLDOWN_MESSAGE =
    "You've requested too many codes. Please try again after 10 minutes.";

interface HandleVerifyResponseOptions {
    /** Flow-navigation advance for this view (each view owns its own instance). */
    advanceFlow: () => Promise<void>;
    /** Clears the view's bound code value (smsCode / emailCode). */
    clearCode: () => void;
    /**
     * Channel-specific handling for the legacy flag-OFF Passed path. Phone just
     * advances; email shows a toast then advances. Must perform its own advance.
     */
    onPassed: () => Promise<void>;
}

interface PerformResendOptions {
    /**
     * When true the 30s anti-spam cooldown guard is skipped. The locked-state
     * "Resend code" path passes true: the current code is already dead, so there
     * is nothing left to rate-limit and forcing a wait would be broken UX.
     */
    bypassCooldown: boolean;
    /** Reads the view's countdown state at call time (own useCountdownTimer instance). */
    isCountdownRunning: () => boolean;
    /** Restarts the view's countdown after a fresh code is issued. */
    start: () => void;
    /** Channel-specific send/resend API call (SendCode vs ResendEmailLoginCode). */
    sendFn: () => Promise<OtpSendResponse>;
    /** Toast shown after a fresh code is issued (channel-specific copy). */
    successToast: string;
    /** Clears the view's bound code value (smsCode / emailCode). */
    clearCode: () => void;
    /** Optional channel-specific side effects on a fresh code (phone: lastDeliveryType + resent flag). */
    onInitialized?: () => void;
}

export function useOtpCodeVerification() {
    const router = useRouter();
    const deviceAuthStore = useDeviceAuthStore();
    const instance = getCurrentInstance();
    const proxy = instance?.proxy;

    // Shared form/verify state.
    const inlineError = ref<string | null>(null);
    // Inline "this code is no longer valid" state: the current code's retries are
    // exhausted but the OTP session is still valid (resends remain). Shown on the
    // SAME screen — we do NOT navigate to mfa-failed for this state.
    const codeLocked = ref(false);
    const isSendingCode = ref(false);

    const hasMultipleChannels = computed(
        () => (deviceAuthStore.availableChannels?.length ?? 0) > 1,
    );

    // Build the "{N} attempts remaining" message after a wrong code. Singular when
    // exactly one attempt is left. Falls back to the generic copy when the bff did
    // not send attemptsRemaining (null/undefined).
    const buildTryAgainMessage = (attemptsRemaining?: number) => {
        if (attemptsRemaining == null) return 'Incorrect code, try again';
        const noun = attemptsRemaining === 1 ? 'attempt' : 'attempts';
        return `Incorrect code. ${attemptsRemaining} ${noun} remaining.`;
    };

    const tryAnotherWay = async () => {
        // Return to channel selection if multiple channels are available
        if (hasMultipleChannels.value) {
            await router.push({ name: 'channel-selection' });
        }
    };

    // Shared post-verify routing. statusCode wins when present (Auth Service made
    // a decision post-token-exchange); otherwise the sub-flow status string is the
    // pre-token-exchange fallback. Channel-specific bits arrive via options.
    const handleVerifyResponse = async (
        response: OtpVerifyResponse,
        { advanceFlow, clearCode, onPassed }: HandleVerifyResponseOptions,
    ): Promise<void> => {
        // Routing — statusCode wins when present (Auth Service made a decision
        // post-token-exchange). handleStatusCode covers 4001 access-denied,
        // 4002 step-up (consumes inline flowSteps), 4005 manual review/pause,
        // 4007 disambiguation, etc.
        if (response.statusCode != null) {
            await handleStatusCode(response, router, advanceFlow);
            return;
        }

        // Sub-flow string fallback — pre-token-exchange outcomes only.
        switch (response.status) {
            case OtpStatus.Passed:
                // Flag-OFF / legacy path: code was accepted but no AS decision
                // was emitted (no statusCode). Channel-specific advance.
                await onPassed();
                return;

            case OtpStatus.TryAgain:
                inlineError.value = buildTryAgainMessage(response.attemptsRemaining);
                clearCode();
                return;

            case OtpStatus.CodeRetriesExhausted:
                // The current code's retries are used up, but the session is
                // still valid (resends remain). Surface the inline locked state
                // on THIS screen — do NOT route to mfa-failed.
                inlineError.value = null;
                clearCode();
                codeLocked.value = true;
                return;

            case OtpStatus.Failed:
                // Retry-exhausted only (NOT RDS-Deny, which now arrives with
                // statusCode 4001 and routes through handleStatusCode above).
                await router.push({ name: 'mfa-failed' });
                return;

            case OtpStatus.Locked:
                // Terminal: OTP session is locked, no new code can be sent.
                // mfa-failed's "Verification limit reached" copy fits.
                await router.push({ name: 'mfa-failed' });
                return;

            case OtpStatus.Cooldown:
                // Recoverable ~10-minute cooldown (resend cap / per-destination issuance
                // limit), NOT a terminal failure. Route to mfa-failed but flag the reason so
                // the page shows the "try again after 10 minutes" copy rather than the
                // terminal "start over" copy used for Failed/Locked.
                await router.push({ name: 'mfa-failed', query: { reason: 'cooldown' } });
                return;

            case OtpStatus.ManualReviewRequired:
                // The OTP was correct; the identity is queued for manual review.
                // Route to the 'pause' view (its copy already covers this state)
                // and NOT 'mfa-failed', which would wrongly tell the user they
                // entered a bad code.
                await router.push({ name: 'pause' });
                return;

            case OtpStatus.DisambiguationRequired:
                // Legacy fallback for responses that omit statusCode 4007.
                if (response.matchedAccounts?.length) {
                    deviceAuthStore.setMatchedAccounts(response.matchedAccounts);
                }
                await router.push({ name: 'select-account' });
                return;

            default:
                proxy?.$apptoast('Unexpected response. Please try again.');
                return;
        }
    };

    // Shared send/resend skeleton. Both verify screens issue a fresh code the same
    // way — anti-spam cooldown guard, in-flight flag, then route on the resulting
    // MfaStatus — differing only in the API call, success copy, and (phone) a few
    // side effects. Channel-specific bits arrive via options so the skeleton lives once.
    const performResend = async ({
        bypassCooldown,
        isCountdownRunning,
        start,
        sendFn,
        successToast,
        clearCode,
        onInitialized,
    }: PerformResendOptions): Promise<void> => {
        if (isSendingCode.value) return;
        if (!bypassCooldown && isCountdownRunning()) {
            proxy?.$apptoast('You must wait the requisite amount of time before requesting a new code.');
            return;
        }

        isSendingCode.value = true;
        inlineError.value = null;

        try {
            const response = await sendFn();
            deviceAuthStore.setMfaStatus(response.status);

            if (response.status === MfaStatus.Initialized) {
                // A fresh code was sent — clear the inline locked state and return to
                // the normal input. The backend resets attempts for the new code, so
                // the counter naturally restarts on the next wrong attempt.
                clearCode();
                codeLocked.value = false;
                onInitialized?.();
                start();
                proxy?.$apptoast(successToast);
                return;
            }

            if (response.status === MfaStatus.Failed) {
                await router.push({ name: 'mfa-failed' });
                return;
            }

            // Cooldown: the issuance cap was hit — a new code can't be sent yet. Surface a
            // clear cooldown message and stay put (a per-destination cooldown leaves any
            // other channel still usable) instead of the misleading generic "try again".
            if (response.status === MfaStatus.Cooldown) {
                proxy?.$apptoast(OTP_COOLDOWN_MESSAGE);
                return;
            }

            proxy?.$apptoast('Unable to send code. Please try again.');
        } catch (err) {
            console.error('[Auth MFE] resend failed:', err);
            proxy?.$apptoast('Unable to send code. Please try again.');
        } finally {
            isSendingCode.value = false;
        }
    };

    return {
        inlineError,
        codeLocked,
        isSendingCode,
        hasMultipleChannels,
        buildTryAgainMessage,
        tryAnotherWay,
        handleVerifyResponse,
        performResend,
    };
}
